import Map from '@arcgis/core/Map';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../types/AppState';

export function useGISLayersOpacity(map: Map | undefined) {
  const layersOpacity = useSelector(
    (state: AppState) => state.layerOpacity.layers,
  );

  // Use a ref to always have the latest layersOpacity in the event handler
  // This way we don't re-attach the event listener on every layersOpacity change
  const layersOpacityRef = useRef(layersOpacity);
  layersOpacityRef.current = layersOpacity;

  // Set up the event listener once when map is available
  useEffect(() => {
    if (!map) {
      return;
    }

    // Listen for new layers being added to the map and apply opacity to them
    // This fixes an issue where we try to apply opacity to layers that are not yet added to the map
    const layerAddHandle = map.layers.on('after-add', event => {
      const addedLayer = event.item;
      const layerOpacityConfig = layersOpacityRef.current[addedLayer.id];
      if (layerOpacityConfig) {
        addedLayer.opacity = layerOpacityConfig.opacity / 100;
      }
    });

    return () => {
      if (layerAddHandle) {
        layerAddHandle.remove();
      }
    };
  }, [map]);

  // Apply opacity changes to all layers whenever layersOpacity changes
  useEffect(() => {
    if (!map) {
      return;
    }

    Object.entries(layersOpacity).forEach(([layerId, { opacity }]) => {
      const matchingLayer = map?.findLayerById(layerId);
      if (matchingLayer) {
        matchingLayer.opacity = opacity / 100;
      }
    });
  }, [layersOpacity, map]);
}
